<template>
	<div
		:id="uid"
		class="split-input"
		:class="{
			'is-disabled': disabled,
			'is-required': isRequired,
			'has-error': showError
		}"
	>
		<Label class="split-input__label" v-if="label" :labelText="label" />

		<div class="split-input__group">
			<Dropdown
				ref="spDropdown"
				class="split-input__dropdown"
				:options="splitOptions"
				:showClearSelectedButton="false"
				:showIErrorIcon="false"
				v-model="dataSplitValue"
				:hasError="showError"
			/>

			<div class="split-input__input-container">
				<input
					ref="spInput"
					class="split-input__input"
					:class="className"
					:id="`${uid}_split_input`"
					:maxLength="maxLength"
					:minLength="minLength"
					:name="name"
					:placeholder="placeholder"
					:required="isRequired"
					:type="inputType"
					:value="dataValue"
					@input="updateValue"
					@focus="$v.value.$touch()"
				/>
				<NotificationIcon
					variant="negative"
					class="split-input__icon-error"
					v-show="showError"
				/>
			</div>
		</div>

		<div class="split-input__text-error" v-show="showError">
			{{ error }}
		</div>
	</div>
</template>

<script>
import Label from '@/components/forms/label/label';
import Dropdown from '@/components/forms/dropdown/dropdown';
import NotificationIcon from '@/components/notifications/notification-icon/notification-icon';

import {
	required,
	minLength,
	maxLength,
	email
} from 'vuelidate/lib/validators';
import _ from 'lodash';

const regExp = /^([0-9]{3} ?|[0-9]{3} ?)[0-9]{3} ?[0-9]{4}/;

export default {
	name: 'SplitInput',
	components: {
		Label,
		Dropdown,
		NotificationIcon
	},
	model: {
		prop: 'value',
		event: 'change'
	},
	props: {
		className: {
			type: String,
			default: ''
		},
		splitOptions: {
			type: Array,
			default: () => []
		},
		splitValue: {
			type: String,
			default: ''
		},
		value: {
			type: String,
			default: ''
		},
		maxLength: {
			type: Number,
			default: 100
		},
		minLength: {
			type: Number,
			default: 0
		}
	},
	data() {
		return {
			dataSplitValue: this.splitValue,
			dataValue: this.value,
			elInput: {}
		};
	},
	computed: {
		inputProps() {
			const opt = this.splitOptions.find(
				o => o.value == this.dataSplitValue
			);
			if (opt && opt.validations) {
				return opt.validations;
			}
			return {};
		},
		inputType() {
			const { inputType } = this.inputProps;
			return inputType || '';
		},
		isRequired() {
			const { required } = this.inputProps;
			return required || false;
		},
		splitSelected() {
			let inputOption = this.splitOptions.find(
				option => option.value === this.dataSplitValue
			);
			return inputOption || {};
		},
		placeholder() {
			const { placeholder } = this.splitSelected;
			return placeholder || '';
		},
		label() {
			const { label } = this.splitSelected;
			return label || '';
		},
		showError() {
			return !!this.error;
		},
		error() {
			if (_.get(this.$v, 'value.$anyError', false)) {
				const value = this.$v.value;
				let msg = '';

				if (value.required === false) {
					msg = 'is required';
				} else if (value.minLength === false) {
					msg = `should have at least ${this.minLength}`;
				} else if (value.maxLength === false) {
					msg = `should have no more than ${this.maxLength}`;
				} else if (value.email === false) {
					msg = 'is not in a valid format';
				}

				return `${this.label || 'Option Selected '} ${msg}`;
			}

			if (
				this.$v.value.required === true &&
				this.inputType === 'phonenumber' &&
				!regExp.test(this.dataValue)
			) {
				return `is not a phone number`;
			}

			return '';
		}
	},
	validations() {
		let result = {};
		if (this.isRequired) {
			result.value = {
				required
			};
		}
		if (this.minLength) {
			result.value = {
				...result.value,
				minLength: minLength(this.minLength)
			};
		}
		if (this.maxLength) {
			result.value = {
				...result.value,
				maxLength: maxLength(this.maxLength)
			};
		}
		if (this.inputType.toLowerCase() === 'email') {
			result.value = {
				...result.value,
				email
			};
		}
		return { ...result };
	},
	methods: {
		updateValue(e) {
			let value = e.target.value;
			this.dataValue = value;
		}
	},
	watch: {
		dataValue: {
			handler(val) {
				if (this.inputType === 'phonenumber') {
					val = Number(val);
				}
				this.$emit('change', val);
			}
		},
		dataSplitValue: {
			handler() {
				this.dataValue = '';
				this.$nextTick(() => {
					this.$refs.spInput.focus();
				});
			}
		}
	}
};
</script>

<style lang="scss">
/**
    * Split Input component
    * Can use SCSS variables = TRUE
*/

@import '@/scss/5-objects/_form.scss';

.split-input {
	position: relative;

	&__group {
		display: grid;
		grid-template-columns: 84px auto;
	}

	&__dropdown {
		.dropdown__input-container {
			border-top-right-radius: 0;
			border-bottom-right-radius: 0;
		}
	}

	&__input-container {
		position: relative;
		display: flex;
		@include textfield;
		border-top-left-radius: 0;
		border-bottom-left-radius: 0;
		border-left: 0;
		align-items: center;

		&:focus-within {
			-o-outline: $focus-outline;
			-moz-outline: $focus-outline;
			outline: $focus-outline;
			outline-offset: $focus-offset;
		}

		@include mq($from: sm-l) {
			min-width: 300px;
		}
	}

	&__input {
		appearance: none;
		outline: none;
		border: none;
		@include input-height;
		cursor: pointer;
		font-size: $input-font-size;
		text-overflow: ellipsis;
		padding: $input-padding-y $input-padding-x;
		width: 100%;
		background-color: transparent;
	}

	/*------------------------------------*\
  #ERROR
\*------------------------------------*/

	&__icon-error {
		margin-right: $gutter-md;
	}

	&__text-error {
		display: block;
		color: $ui-negative;
		font-size: $label-font-size - 2;
		margin-top: $gutter-sm;
	}

	&.has-error {
		.split-input__input-container,
		.dropdown__input-container {
			border-color: $ui-negative;
		}
		.split-input__input-container:focus-within {
			outline-color: $ui-negative;
		}
	}
}
</style>
